import {
  Box,
  Dialog,
  DialogContent,
  DialogTitle,
  LinearProgress,
  Paper,
  Stack,
  Typography,
} from '@mui/material';

type SegmentAttentionProgressDialogProps = {
  open: boolean;
  onClose: () => void;
  title?: string;
  progressCurrent: number;
  progressTotal: number;
  progressPercentage: number;
  latestStatusText: string;
  latestStepLabel: string;
  lastUpdatedAt?: number | null;
};

export default function SegmentAttentionProgressDialog({
  open,
  onClose,
  title = 'Segment retry progress',
  progressCurrent,
  progressTotal,
  progressPercentage,
  latestStatusText,
  latestStepLabel,
  lastUpdatedAt,
}: SegmentAttentionProgressDialogProps) {
  const denominator = Math.max(progressTotal, 1);
  const numerator = Math.min(
    denominator,
    Math.max(
      progressCurrent,
      Math.round((progressPercentage / 100) * denominator),
    ),
  );

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{ sx: { borderRadius: 2, maxHeight: '90vh' } }}
    >
      <DialogTitle sx={{ fontWeight: 700 }}>{title}</DialogTitle>
      <DialogContent dividers>
        <Stack spacing={2}>
          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 0.75 }}>
              {numerator}/{denominator} segments processed ({progressPercentage}%)
            </Typography>
            <LinearProgress
              variant="determinate"
              value={progressPercentage}
              sx={{ height: 7, borderRadius: 999 }}
            />
          </Box>

          <Paper variant="outlined" sx={{ p: 2, bgcolor: 'grey.50' }}>
            <Stack spacing={1}>
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  gap: 1,
                  flexWrap: 'wrap',
                }}
              >
                <Typography variant="caption" color="text.secondary" fontWeight={700}>
                  {latestStepLabel || 'Current step'}
                </Typography>
                {lastUpdatedAt ? (
                  <Typography variant="caption" color="text.secondary">
                    {new Date(lastUpdatedAt).toLocaleTimeString()}
                  </Typography>
                ) : null}
              </Box>
              <Typography variant="body2" sx={{ wordBreak: 'break-word', fontWeight: 500 }}>
                {latestStatusText || 'Waiting for progress updates…'}
              </Typography>
            </Stack>
          </Paper>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
